import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  EnvelopeIcon,
  ClockIcon,
  BuildingOfficeIcon,
  UserCircleIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';

const Contact = () => {
  return (
    <>
      <Navbar />
      <main id="main-content" className="bg-white">

      {/* Hero Section */}
      <section className="section-padding band-light text-brand-text-primary pt-24 md:pt-32 relative overflow-hidden">
        <div className="content-container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="relative mb-8">
              <span className="inline-block bg-white shadow-sm text-brand-text-primary px-6 py-3 text-sm font-medium tracking-wider border border-brand-primary/20">
                Get In Touch
              </span>
            </div>
            <h1 className="text-display font-display mb-8 relative">
              <span className="relative">
                Contact Us
              </span>
            </h1>
            <p className="text-xl leading-relaxed max-w-3xl mx-auto">
              We're here to help! Reach out to our dedicated team at PS/MS 108 School of Authors.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="section-padding bg-white">
        <div className="content-container">
          <div className="max-w-6xl mx-auto">
            {/* Leadership Contact Cards */}
            <h2 className="sr-only">School Leadership</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-16">
              {/* Principal Card */}
              <div className="bg-brand-primary/5 p-8 md:p-10 rounded-2xl border border-brand-primary/20 hover:border-brand-primary/40 transition-all duration-300 hover:shadow-xl">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <UserCircleIcon className="w-9 h-9 text-brand-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-display text-heading mb-2">Principal</h3>
                    <p className="text-brand-primary font-semibold text-xl">Jenn Tegano</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <EnvelopeIcon className="w-5 h-5 text-brand-accent mt-1 flex-shrink-0" />
                    <a
                      href="mailto:jtegano@schools.nyc.gov"
                      className="text-body hover:text-brand-primary transition-colors duration-200 break-all"
                    >
                      jtegano@schools.nyc.gov
                    </a>
                  </div>
                </div>
              </div>

              {/* Assistant Principal Card */}
              <div className="bg-purple-50 p-8 md:p-10 rounded-2xl border border-purple-200 hover:border-purple-300 transition-all duration-300 hover:shadow-xl">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <UserCircleIcon className="w-9 h-9 text-purple-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-display text-heading mb-2">Assistant Principal</h3>
                    <p className="text-purple-700 font-semibold text-xl">Dr. Nadine Sinclair</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <EnvelopeIcon className="w-5 h-5 text-brand-accent mt-1 flex-shrink-0" />
                    <a
                      href="mailto:Nsinclair@schools.nyc.gov"
                      className="text-body hover:text-purple-700 transition-colors duration-200 break-all"
                    >
                      Nsinclair@schools.nyc.gov
                    </a>
                  </div>
                </div>
              </div>

              {/* Parent Coordinator Card */}
              <div className="bg-brand-secondary/5 p-8 md:p-10 rounded-2xl border border-brand-secondary/20 hover:border-brand-secondary/40 transition-all duration-300 hover:shadow-xl">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-16 h-16 bg-brand-secondary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <UserCircleIcon className="w-9 h-9 text-brand-secondary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-display text-heading mb-2">Parent Coordinator</h3>
                    <p className="text-brand-secondary font-semibold text-xl">Anna Torres</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <EnvelopeIcon className="w-5 h-5 text-brand-accent mt-1 flex-shrink-0" />
                    <a
                      href="mailto:atorres143@schools.nyc.gov"
                      className="text-body hover:text-brand-secondary transition-colors duration-200 break-all"
                    >
                      atorres143@schools.nyc.gov
                    </a>
                  </div>
                  <div className="flex items-start space-x-3">
                    <PhoneIcon className="w-5 h-5 text-brand-accent mt-1 flex-shrink-0" />
                    <a
                      href="tel:+12128605803,3002"
                      className="text-body hover:text-brand-secondary transition-colors duration-200"
                    >
                      (212) 860-5803 ext. 3002
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* School Hours Section */}
            <div className="bg-gray-50 rounded-2xl p-8 md:p-12 relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-accent/5 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-brand-primary/5 rounded-full -ml-12 -mb-12"></div>

              <div className="relative z-10">
                <div className="text-center mb-10">
                  <div className="w-20 h-20 bg-brand-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <ClockIcon className="w-10 h-10 text-brand-accent" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-display text-heading mb-3">
                    School Hours
                  </h2>
                  <div className="w-16 h-1 bg-brand-accent/30 mx-auto"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                  {/* School Hours Card */}
                  <div className="bg-white p-6 md:p-8 rounded-xl border-l-4 border-brand-primary shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center mb-4">
                      <BuildingOfficeIcon className="w-6 h-6 text-brand-primary mr-3" />
                      <h3 className="font-display text-heading text-xl">School Hours</h3>
                    </div>
                    <div className="space-y-2">
                      <p className="text-body">
                        <span className="font-semibold text-brand-primary">Monday - Friday</span>
                      </p>
                      <p className="text-2xl font-bold text-heading">8:10 AM - 2:30 PM</p>
                    </div>
                  </div>

                  {/* Office Hours Card */}
                  <div className="bg-white p-6 md:p-8 rounded-xl border-l-4 border-brand-secondary shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center mb-4">
                      <ClockIcon className="w-6 h-6 text-brand-secondary mr-3" />
                      <h3 className="font-display text-heading text-xl">Office Hours</h3>
                    </div>
                    <div className="space-y-2">
                      <p className="text-body">
                        <span className="font-semibold text-brand-secondary">Monday - Friday</span>
                      </p>
                      <p className="text-2xl font-bold text-heading">8:30 AM - 3:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-gradient-to-b from-white to-gray-50">
        <div className="content-container">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white p-8 md:p-12 rounded-2xl border border-gray-200 shadow-lg">
              <div className="mb-8">
                <h3 className="text-3xl md:text-4xl font-display text-brand-primary mb-4">
                  Send Us a Message
                </h3>
                <p className="text-lg text-body">
                  If you have any questions or concerns, please fill out the form below and we'll get back to you as soon as possible.
                </p>
              </div>

              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Full Name */}
                  <div>
                    <label htmlFor="fullName" className="block text-lg font-semibold text-heading mb-2">
                      Full Name<span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      required
                      aria-required="true"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                    />
                  </div>

                  {/* Email Address */}
                  <div>
                    <label htmlFor="email" className="block text-lg font-semibold text-heading mb-2">
                      Email Address<span className="text-red-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      aria-required="true"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Phone Number */}
                  <div>
                    <label htmlFor="phone" className="block text-lg font-semibold text-heading mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                    />
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="subject" className="block text-lg font-semibold text-heading mb-2">
                      Subject<span className="text-red-600">*</span>
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      aria-required="true"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all appearance-none bg-white"
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="enrollment">Enrollment</option>
                      <option value="academics">Academics</option>
                      <option value="events">Events</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-lg font-semibold text-heading mb-2">
                    Message<span className="text-red-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    required
                    aria-required="true"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all resize-none"
                  ></textarea>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="bg-brand-primary text-white font-semibold px-10 py-4 rounded-lg hover:bg-brand-secondary transition-all duration-300 shadow-lg hover:shadow-xl text-lg"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="section-padding bg-gray-50">
        <div className="content-container">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-8 md:p-12 rounded-2xl border border-gray-200 shadow-lg">
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-display text-heading mb-4">
                  Additional Contact Information
                </h3>
                <div className="w-16 h-1 bg-brand-accent/30 mx-auto mb-6"></div>
              </div>

              <div className="space-y-4 text-center md:text-left">
                <div className="flex flex-col md:flex-row md:items-center justify-center md:justify-start space-y-2 md:space-y-0 md:space-x-3">
                  <span className="font-semibold text-brand-primary">Address:</span>
                  <span className="text-body">1615 Madison Avenue, New York, NY 10029</span>
                </div>

                <div className="flex flex-col md:flex-row md:items-center justify-center md:justify-start space-y-2 md:space-y-0 md:space-x-3">
                  <span className="font-semibold text-brand-primary">Phone:</span>
                  <a href="tel:+12128605803" className="text-body hover:text-brand-primary transition-colors">
                    (212) 860-5803
                  </a>
                </div>

                <div className="flex flex-col md:flex-row md:items-center justify-center md:justify-start space-y-2 md:space-y-0 md:space-x-3">
                  <span className="font-semibold text-brand-primary">Fax:</span>
                  <span className="text-body">(212) 860-6095</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding band-warm text-brand-text-primary">
        <div className="content-container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-display font-display mb-6">
              Ready to Connect?
            </h2>
            <p className="text-xl leading-relaxed mb-8">
              Whether you're a current family, prospective student, or community member, we'd love to hear from you.
            </p>
            <p className="text-body">
              Feel free to reach out via email or phone during our office hours. We look forward to connecting with you!
            </p>
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </>
  );
};

export default Contact;
